/**
 * Phase 129 (Plan 129-05, D-03, D-22) — the guard of `/projects/:id/edit`.
 *
 *   ProtectedRoute + team gate: admin and finance pass as today; everyone else needs a 200 from
 *   the server's membership probe, a 403 sends them away.
 *
 * Presentation only: the server's team gate on every Engineering route is the authority.
 */
import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../services/authContext';
import { useProjectEngineeringAccess } from '../../hooks/useProjectEngineering';
import { LoadingPage } from '../common/Spinner';
import { ProtectedRoute } from './ProtectedRoute';

export function ProjectEditRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <ProjectTeamGate>{children}</ProjectTeamGate>
    </ProtectedRoute>
  );
}

function ProjectTeamGate({ children }: { children: ReactNode }) {
  const { isAdmin, isFinance } = useAuth();
  const { id } = useParams<{ id: string }>();
  const projectId = id ? parseInt(id, 10) : NaN;
  const validId = Number.isFinite(projectId) && projectId > 0;
  // Admin and finance are project-independent passes today (MKS-94); only the rest are probed.
  const passes = isAdmin || isFinance;

  const access = useProjectEngineeringAccess(validId ? projectId : undefined, !passes);

  if (!validId) return <Navigate to="/unauthorized" replace />;
  if (passes) return <>{children}</>;
  if (access.isSuccess) return <>{children}</>;
  if (access.isError) return <Navigate to="/unauthorized" replace />;
  return <LoadingPage />;
}
